// Wizard di creazione azienda condiviso (punto 174):
// - mode="platform": console Super Admin, con step di selezione del consulente;
// - mode="consultant": Consulenti (header "Nuova azienda"), il creatore è
//   auto-assegnato come LEAD_CONSULTANT e lo step consulente è omesso.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { assignmentRoleLabel } from "@/lib/strings";

const PLATFORM_STEPS = ["Anagrafica", "Consulente", "Admin azienda", "Periodo & Moduli"];
const CONSULTANT_STEPS = ["Anagrafica", "Admin azienda", "Periodo & Moduli"];
const ASSIGNMENT_ROLES = ["LEAD_CONSULTANT", "CONSULTANT", "REVIEWER", "SPECIALIST", "READ_ONLY"];
const MODULES = ["VSME", "MEF", "ENVIRONMENTAL", "SOCIAL", "GOVERNANCE",
  "JOBTIMEHR", "DOCUMENT_AI", "EVA", "EXTERNAL_QUESTIONNAIRES", "DATA_ROOM",
  "SCORE", "DATA_QUALITY", "IMPROVEMENT_PLAN", "SIMULATOR", "REPORTING", "PORTFOLIO"];
const EMPTY_FORM = { denominazione: "", partita_iva: "", codice_fiscale: "",
  nazione: "Italia", citta: "", codice_ateco: "", settore: "",
  consultant_user_id: "", consultant_role: "LEAD_CONSULTANT",
  admin_email: "", admin_full_name: "", reporting_year: "", modules: {} };

export function CompanyWizardDialog({ open, onOpenChange, mode = "platform", onCreated }) {
  const isPlatform = mode === "platform";
  const STEPS = isPlatform ? PLATFORM_STEPS : CONSULTANT_STEPS;
  const [step, setStep] = useState(0);
  const [consultants, setConsultants] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const { changeCompany } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm(EMPTY_FORM);
    setCreated(null);
    if (isPlatform && !consultants.length) {
      api.get("/admin/platform/consultants")
        .then(({ data: c }) => setConsultants(c.items || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        admin_email: form.admin_email || null,
        reporting_year: form.reporting_year ? parseInt(form.reporting_year, 10) : null,
        modules: Object.keys(form.modules).length ? form.modules : null };
      if (isPlatform) {
        payload.consultant_user_id = form.consultant_user_id || null;
      } else {
        delete payload.consultant_user_id;
        delete payload.consultant_role;
      }
      const { data: r } = await api.post(
        isPlatform ? "/admin/platform/companies" : "/companies/wizard", payload);
      setCreated(r);
      toast.success("Azienda creata in PENDING_SETUP", {
        description: isPlatform
          ? "Completa l'onboarding e attivala dal dettaglio."
          : "Sei stato assegnato come Lead Consultant." });
      if (!isPlatform) await refresh();
      onCreated?.(r);
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const openCompany = () => {
    changeCompany(created.company.id);
    onOpenChange(false);
    navigate("/azienda");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="company-wizard">
        <DialogHeader>
          <DialogTitle>Nuovo Cliente — passo {step + 1} di {STEPS.length}: {STEPS[step]}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="company-wizard-done">
            <p className="text-sm text-slate-700">
              <strong>{created.company.denominazione}</strong> creata in stato{" "}
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">PENDING_SETUP</Badge>
            </p>
            {created.activation_link && (
              <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-2">
                <code className="text-xs text-green-900 truncate flex-1">
                  {`${window.location.origin}${created.activation_link}`}</code>
                <Button size="sm" variant="outline" className="h-7"
                  data-testid="wizard-copy-link"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `${window.location.origin}${created.activation_link}`);
                      toast.success("Link copiato");
                    } catch {
                      toast.info("Copia manualmente il link selezionandolo");
                    }
                  }}>
                  <Copy className="w-3 h-3 mr-1" aria-hidden="true" /> Copia invito admin
                </Button>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}
                data-testid="wizard-close">Chiudi</Button>
              {isPlatform ? (
                <Link to={`/admin/platform/aziende/${created.company.id}`}>
                  <Button className="bg-green-700 hover:bg-green-800"
                    data-testid="wizard-goto-detail">Apri e attiva →</Button>
                </Link>
              ) : (
                <Button className="bg-green-700 hover:bg-green-800" onClick={openCompany}
                  data-testid="wizard-goto-company">Apri azienda →</Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {STEPS[step] === "Anagrafica" && (
              <div className="grid grid-cols-2 gap-3">
                {!isPlatform && (
                  <p className="col-span-2 text-xs text-slate-500 rounded-md bg-slate-50 border border-slate-200 p-2">
                    Verrai assegnato automaticamente come Lead Consultant dell'azienda.
                  </p>
                )}
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="wz-den">Denominazione *</Label>
                  <Input id="wz-den" value={form.denominazione} data-testid="wizard-denominazione"
                    onChange={(e) => setForm((f) => ({ ...f, denominazione: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wz-piva">P.IVA</Label>
                  <Input id="wz-piva" value={form.partita_iva} data-testid="wizard-piva"
                    onChange={(e) => setForm((f) => ({ ...f, partita_iva: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wz-cf">Codice fiscale</Label>
                  <Input id="wz-cf" value={form.codice_fiscale} data-testid="wizard-cf"
                    onChange={(e) => setForm((f) => ({ ...f, codice_fiscale: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wz-city">Città</Label>
                  <Input id="wz-city" value={form.citta} data-testid="wizard-citta"
                    onChange={(e) => setForm((f) => ({ ...f, citta: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wz-ateco">ATECO</Label>
                  <Input id="wz-ateco" value={form.codice_ateco} data-testid="wizard-ateco"
                    onChange={(e) => setForm((f) => ({ ...f, codice_ateco: e.target.value }))} />
                </div>
              </div>
            )}
            {STEPS[step] === "Consulente" && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Consulente assegnato</Label>
                  <Select value={form.consultant_user_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, consultant_user_id: v }))}>
                    <SelectTrigger data-testid="wizard-consultant-select">
                      <SelectValue placeholder="Seleziona consulente (opzionale)" /></SelectTrigger>
                    <SelectContent>
                      {consultants.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.full_name} · {c.organization_name || "—"}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Ruolo assegnazione</Label>
                  <Select value={form.consultant_role}
                    onValueChange={(v) => setForm((f) => ({ ...f, consultant_role: v }))}>
                    <SelectTrigger data-testid="wizard-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSIGNMENT_ROLES.map((r) => <SelectItem key={r} value={r}>{assignmentRoleLabel(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {STEPS[step] === "Admin azienda" && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  L'admin aziendale riceve un invito e imposta la propria password.</p>
                <div className="space-y-1">
                  <Label htmlFor="wz-admin-name">Nome admin azienda</Label>
                  <Input id="wz-admin-name" value={form.admin_full_name} data-testid="wizard-admin-name"
                    onChange={(e) => setForm((f) => ({ ...f, admin_full_name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wz-admin-email">Email admin azienda</Label>
                  <Input id="wz-admin-email" type="email" value={form.admin_email} data-testid="wizard-admin-email"
                    onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} />
                </div>
              </div>
            )}
            {STEPS[step] === "Periodo & Moduli" && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="wz-year">Anno reporting period</Label>
                  <Input id="wz-year" type="number" min="2020" max="2100"
                    value={form.reporting_year} data-testid="wizard-year"
                    placeholder="es. 2025 (vuoto = nessun periodo)"
                    onChange={(e) => setForm((f) => ({ ...f, reporting_year: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Moduli da DISATTIVARE per questa azienda</Label>
                  <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                    {MODULES.map((m) => (
                      <label key={m} className="flex items-center gap-2 text-xs text-slate-700">
                        <input type="checkbox" className="accent-green-700"
                          data-testid={`wizard-module-${m}`}
                          checked={form.modules[m] === false}
                          onChange={(e) => setForm((f) => ({
                            ...f, modules: { ...f.modules, [m]: e.target.checked ? false : true }}))} />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="outline" disabled={step === 0}
                onClick={() => setStep((s) => s - 1)} data-testid="wizard-prev">Indietro</Button>
              {step < STEPS.length - 1 ? (
                <Button className="bg-green-700 hover:bg-green-800"
                  disabled={step === 0 && !form.denominazione}
                  onClick={() => setStep((s) => s + 1)} data-testid="wizard-next">Avanti</Button>
              ) : (
                <Button className="bg-green-700 hover:bg-green-800" onClick={submit}
                  disabled={saving} data-testid="wizard-submit">
                  {saving ? "Creazione…" : "Crea azienda"}</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
